import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonPage,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { contacts, user } from '../services/api.ts';

interface Contact {
  id: string;
  name: string;
  email: string;
  company: string;
  title: string;
  link?: string;
}

interface Job {
  id: string;
  title: string;
  link: string;
  company_name: string;
}

interface UserProfile {
  name: string;
  linkedin_url: string;
}

const DraftEmail: React.FC<{ isReferral: boolean }> = ({ isReferral }) => {
  const { id, jobId } = useParams<{ id: string; jobId?: string }>();
  const [present] = useIonToast();
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState<Contact | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const loadEmailData = async () => {
      try {
        setLoading(true);

        // Fetch contact, user profile, and jobs (if referral) in parallel
        const [contactData, profileData, jobsData] = await Promise.all([
          contacts.getContactById(id),
          user.getProfile(),
          isReferral && jobId ? contacts.getContactJobs(id) : null
        ]);

        setContact(contactData);
        setUserProfile(profileData);

        if (isReferral && jobId && jobsData) {
          const relatedJob = jobsData.find(j => j.id === jobId);
          setJob(relatedJob || null);

          if (relatedJob) {
            setSubject(`Referral Request for ${relatedJob.title}`);
            setMessage(
              `Dear ${contactData.name},\n\n` +
              `I came across an interesting position for ${relatedJob.title} at ${relatedJob.company_name} ` +
              `that aligns well with my experience. Here's the job link:\n` +
              `${relatedJob.link}\n\n` +
              `Would you be open to discussing this opportunity or providing guidance on the application process? ` +
              `I'd greatly appreciate your insights.\n\n` +
              `Best regards,\n${profileData.name}\nLinkedIn: ${profileData.linkedin_url}`
            );
          }
        } else {
          setSubject('Connecting on LinkedIn');
          setMessage(
            `Dear ${contactData.name},\n\n` +
            `I noticed we connected on LinkedIn and wanted to reach out. I'd love to discuss potential opportunities at ${contactData.company}. ` +
            `Let me know if you'd be available for a quick chat.\n\n` +
            `Best regards,\n${profileData.name}\nLinkedIn: ${profileData.linkedin_url}`
          );
        }
      } catch (error: any) {
        present({
          message: error.response?.data?.detail || 'Failed to load data',
          duration: 3000,
          position: 'bottom',
          color: 'danger'
        });
      } finally {
        setLoading(false);
      }
    };

    loadEmailData();
  }, [id, jobId, isReferral, present]);

  const handleSend = async () => {
    if (!contact?.email || !subject || !message) {
      present({
        message: 'Please fill in all fields',
        duration: 2000,
        color: 'warning'
      });
      return;
    }

    // Note: Email endpoint not implemented yet
    present({
      message: 'Email functionality coming soon!',
      duration: 2000,
      color: 'primary'
    });
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref={`/app/network/contact/${id}`} />
            </IonButtons>
            <IonTitle>{isReferral ? 'Draft Referral Request' : 'Compose Email'}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}>
            <IonSpinner />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!contact || (isReferral && !job)) return null;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/app/network/contact/${id}`} />
          </IonButtons>
          <IonTitle>{isReferral ? 'Draft Referral Request' : 'Compose Email'}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <IonItem>
          <IonLabel position="stacked">To:</IonLabel>
          <IonInput value={contact.email} readonly />
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">Subject:</IonLabel>
          <IonInput
            value={subject}
            onIonInput={e => setSubject(e.detail.value!)}
            placeholder="Enter subject"
          />
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">Message:</IonLabel>
          <IonTextarea
            rows={12}
            value={message}
            onIonInput={e => setMessage(e.detail.value!)}
            placeholder="Type your message here"
          />
        </IonItem>
        <div className="ion-padding">
          <IonButton expand="block" onClick={handleSend}>
            Send
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default DraftEmail;